/**
 * La fiche d'un employé — chunk 9.BK.1.
 *
 * Une carte par personne, pas une ligne de tableau : 22 colonnes en tableau sont
 * illisibles sur un téléphone, et c'est sur un téléphone qu'une adjointe remplit
 * une liste entre deux appels.
 *
 * ## Enregistrement
 *
 * À la sortie du champ (`blur`), pas à la frappe : une écriture par caractère
 * saturerait la limitation de débit et n'apporterait rien. L'état de la sauvegarde
 * est **annoncé**, jamais silencieux — « Enregistrement… », « Enregistré », ou une
 * erreur qui garde la valeur saisie à l'écran plutôt que de la perdre.
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { CensusIssue } from "../../../lib/queries/census";

import {
  CHAMPS_DEPENDANT,
  CHAMPS_FACULTATIFS,
  CHAMPS_PAIE,
  CHAMPS_REQUIS,
  type Champ,
} from "./champs";
import { CoverageChoice } from "./CoverageChoice";

export type EtatSauvegarde = "idle" | "saving" | "saved" | "error";

const inputCls =
  "mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:bg-slate-50 disabled:text-slate-500";

function ChampSaisie({
  champ,
  prefixe,
  valeur,
  probleme,
  lectureSeule,
  onChange,
  onBlur,
}: {
  champ: Champ;
  prefixe: string;
  valeur: string;
  probleme: string | null;
  lectureSeule: boolean;
  onChange: (v: string) => void;
  onBlur: () => void;
}) {
  const { t } = useTranslation();
  const id = `${prefixe}-${champ.cle}`;
  const idErreur = `${id}-err`;
  return (
    <div>
      <label htmlFor={id} className="block text-xs font-medium text-slate-600">
        {t(`census.fields.${champ.cle}`)}
      </label>
      <input
        id={id}
        type={champ.type === "number" ? "text" : champ.type}
        inputMode={champ.type === "number" ? "decimal" : undefined}
        autoComplete="off"
        spellCheck={champ.type === "email" || champ.type === "tel" ? false : undefined}
        disabled={lectureSeule}
        value={valeur}
        aria-invalid={probleme ? true : undefined}
        aria-describedby={probleme ? idErreur : undefined}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className={inputCls}
      />
      {probleme ? (
        <p id={idErreur} className="mt-1 text-xs text-red-600">
          {probleme}
        </p>
      ) : null}
    </div>
  );
}

export type FicheEmployeProps = {
  readonly index: number;
  readonly rowId: string;
  readonly valeurs: Record<string, string>;
  readonly dependants: ReadonlyArray<{ rowId: string; valeurs: Record<string, string> }>;
  readonly issues: readonly CensusIssue[];
  readonly lectureSeule: boolean;
  readonly etat: EtatSauvegarde;
  readonly onChampChange: (cle: string, v: string) => void;
  readonly onEnregistrer: () => void;
  readonly onRetirer: () => void;
  readonly onDependantChange: (rowId: string, cle: string, v: string) => void;
  readonly onDependantEnregistrer: (rowId: string) => void;
  readonly onDependantAjouter: () => void;
  readonly onDependantRetirer: (rowId: string) => void;
};

export function FicheEmploye(props: FicheEmployeProps): JSX.Element {
  const { t } = useTranslation();
  const [detailsOuverts, setDetailsOuverts] = useState(false);
  const { valeurs, issues, lectureSeule } = props;

  /** Le message du serveur pour un champ, ou `null`. Rien n'est recalculé ici. */
  const probleme = (rowId: string, cle: string): string | null => {
    const i = issues.find((x) => x.client_row_id === rowId && x.field === cle);
    return i ? t(`census.issues.${i.reason}`) : null;
  };

  const nom = [valeurs.first_name, valeurs.last_name].filter(Boolean).join(" ");
  const titre = nom || t("census.public.employee_index", { n: props.index + 1 });

  const rendre = (champs: readonly Champ[]) =>
    champs.map((champ) =>
      champ.cle === "coverage_choice" ? (
        <CoverageChoice
          key={champ.cle}
          id={`p-${props.rowId}-coverage_choice`}
          value={valeurs.coverage_choice ?? ""}
          readOnly={lectureSeule}
          error={probleme(props.rowId, "coverage_choice")}
          relatedErrorId={
            probleme(props.rowId, "coverage") ? `p-${props.rowId}-coverage-err` : undefined
          }
          onChange={(v) => props.onChampChange("coverage_choice", v)}
          onBlur={props.onEnregistrer}
        />
      ) : (
        <ChampSaisie
          key={champ.cle}
          champ={champ}
          prefixe={`p-${props.rowId}`}
          valeur={valeurs[champ.cle] ?? ""}
          probleme={probleme(props.rowId, champ.cle)}
          lectureSeule={
            lectureSeule || (champ.cle === "coverage" && Boolean(valeurs.coverage_choice))
          }
          onChange={(v) => props.onChampChange(champ.cle, v)}
          onBlur={props.onEnregistrer}
        />
      ),
    );

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-sm font-semibold text-slate-900">{titre}</h3>
        <div className="flex items-center gap-2">
          <EtatSauvegardeBadge etat={props.etat} />
          {lectureSeule ? null : (
            <button
              type="button"
              onClick={props.onRetirer}
              className="focus-visible:ring-brand-500 touch-manipulation rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2"
            >
              {t("census.public.remove_employee", { name: nom || t("census.public.unnamed") })}
            </button>
          )}
        </div>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">{rendre(CHAMPS_REQUIS)}</div>

      <fieldset className="mt-4">
        <legend className="text-xs font-medium uppercase tracking-wide text-slate-400">
          {t("census.public.remuneration")}
        </legend>
        <p className="mt-0.5 text-xs text-slate-500">{t("census.public.remuneration_hint")}</p>
        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">{rendre(CHAMPS_PAIE)}</div>
        {probleme(props.rowId, "annual_salary") ? null : null}
      </fieldset>

      <div className="mt-4">
        <button
          type="button"
          aria-expanded={detailsOuverts}
          onClick={() => setDetailsOuverts((v) => !v)}
          className="focus-visible:ring-brand-500 text-brand-600 touch-manipulation rounded-md text-xs font-medium hover:underline focus-visible:outline-none focus-visible:ring-2"
        >
          {detailsOuverts ? "− " : "+ "}
          {t("census.public.optional_details")}
        </button>
        {detailsOuverts ? (
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {rendre(CHAMPS_FACULTATIFS)}
          </div>
        ) : null}
      </div>

      <div className="mt-5 border-t border-slate-100 pt-4">
        <h4 className="text-xs font-medium uppercase tracking-wide text-slate-400">
          {t("census.public.dependents_title")}
        </h4>
        {props.dependants.map((d) => (
          <div key={d.rowId} className="mt-3 rounded-lg bg-slate-50 p-3">
            <div className="flex justify-end">
              {lectureSeule ? null : (
                <button
                  type="button"
                  onClick={() => props.onDependantRetirer(d.rowId)}
                  className="focus-visible:ring-brand-500 touch-manipulation rounded-md px-2 py-1 text-xs text-slate-500 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2"
                >
                  {t("census.public.remove_dependent")}
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {CHAMPS_DEPENDANT.map((champ) => (
                <ChampSaisie
                  key={champ.cle}
                  champ={champ}
                  prefixe={`d-${d.rowId}`}
                  valeur={d.valeurs[champ.cle] ?? ""}
                  probleme={probleme(d.rowId, champ.cle)}
                  lectureSeule={lectureSeule}
                  onChange={(v) => props.onDependantChange(d.rowId, champ.cle, v)}
                  onBlur={() => props.onDependantEnregistrer(d.rowId)}
                />
              ))}
            </div>
          </div>
        ))}
        {lectureSeule ? null : (
          <button
            type="button"
            onClick={props.onDependantAjouter}
            className="focus-visible:ring-brand-500 mt-3 touch-manipulation rounded-md border border-dashed border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
          >
            {t("census.public.add_dependent")}
          </button>
        )}
      </div>
    </section>
  );
}

/**
 * L'état d'une sauvegarde, dit à voix haute.
 *
 * `aria-live="polite"` : une personne au lecteur d'écran doit savoir que sa
 * saisie est partie. Sans ça, « aucune perte silencieuse » ne vaudrait que pour
 * les voyants.
 */
function EtatSauvegardeBadge({ etat }: { etat: EtatSauvegarde }): JSX.Element {
  const { t } = useTranslation();
  const texte =
    etat === "saving"
      ? t("census.public.saving")
      : etat === "saved"
        ? t("census.public.saved")
        : etat === "error"
          ? t("census.public.save_failed")
          : "";
  return (
    <span
      aria-live="polite"
      className={`text-xs ${etat === "error" ? "font-medium text-red-600" : "text-slate-400"}`}
    >
      {texte}
    </span>
  );
}
